import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  Card,
  CardContent,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Close,
  Place,
  Navigation,
} from "@mui/icons-material";
import {
  GoogleMap,
  DirectionsRenderer,
  Marker,
  // ❌ ELIMINAR useJsApiLoader de aquí
} from "@react-google-maps/api";

const MapaDireccionesModal = ({ open, onClose, direcciones, latitudSuc, longitudSuc }) => {
  
  const theme = useTheme();

  // ❌ ELIMINAR completamente useJsApiLoader de aquí
  // const { isLoaded, loadError } = useJsApiLoader({ ... });

  const [ubicacionInicio, setUbicacionInicio] = useState(null);
  const [directions, setDirections] = useState(null);
  const [map, setMap] = useState(null);
  const [googleLoaded, setGoogleLoaded] = useState(false); // ✅ Nuevo estado

  // ✅ Verificar cuando Google Maps está disponible
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setGoogleLoaded(true);
      }
    };

    // Verificar inmediatamente
    checkGoogleMaps();

    // Si no está disponible, verificar periódicamente
    const interval = setInterval(() => {
      checkGoogleMaps();
    }, 500);

    // Limpiar intervalo cuando se desmonte el componente
    return () => clearInterval(interval);
  }, []);

  // Obtener ubicación de sucursal o dispositivo
  useEffect(() => {
    if (latitudSuc && longitudSuc) {
      setUbicacionInicio({ lat: parseFloat(latitudSuc), lng: parseFloat(longitudSuc) });
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUbicacionInicio({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        () => setUbicacionInicio({ lat: -32.95, lng: -60.66 })
      );
    } else {
      setUbicacionInicio({ lat: -32.95, lng: -60.66 });
    }
  }, [latitudSuc, longitudSuc]);

const crearIcono = (numero, color = "#2196f3", esInicio = false) => {
  // VERIFICACIÓN MÁS ESTRICTA
  if (!window.google || !window.google.maps || !window.google.maps.Size) {
    console.warn('Google Maps API not fully loaded when creating icon');
    return null;
  }

  const tamaño = esInicio ? 50 : 42;
  const radio = esInicio ? 22 : 18;
  
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="${tamaño}" height="${tamaño}">
        <circle cx="${tamaño/2}" cy="${tamaño/2}" r="${radio}" fill="${color}" 
                stroke="white" stroke-width="3" opacity="0.95"/>
        <text x="${tamaño/2}" y="${tamaño/2 + 5}" text-anchor="middle" 
              font-size="${esInicio ? '16' : '14'}" font-weight="bold" 
              fill="white" font-family="Arial, sans-serif">
          ${numero}
        </text>
      </svg>
    `)}`,
    scaledSize: new window.google.maps.Size(tamaño, tamaño),
    anchor: new window.google.maps.Point(tamaño/2, tamaño/2),
  };
}; // Solo se recrea cuando googleLoaded cambia

  // Calcular distancia euclidiana mejorada
  const calcularDistancia = (punto1, punto2) => {
    const R = 6371;
    const lat1 = punto1.latitud * Math.PI / 180;
    const lat2 = punto2.latitud * Math.PI / 180;
    const deltaLat = (punto2.latitud - punto1.latitud) * Math.PI / 180;
    const deltaLng = (punto2.longitud - punto1.longitud) * Math.PI / 180;

    const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng/2) * Math.sin(deltaLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  const calcularRutaOptima = (puntos, inicio) => {
    if (!puntos || puntos.length === 0) return [];

    const puntoMasLejano = [...puntos].sort((a, b) => {
      const distA = calcularDistancia({ latitud: inicio.lat, longitud: inicio.lng }, a);
      const distB = calcularDistancia({ latitud: inicio.lat, longitud: inicio.lng }, b);
      return distB - distA;
    })[0];

    const puntosRestantes = [...puntos];
    const ruta = [puntoMasLejano];
    puntosRestantes.splice(puntosRestantes.findIndex(
      p => p.latitud === puntoMasLejano.latitud && p.longitud === puntoMasLejano.longitud
    ), 1);

    while (puntosRestantes.length > 0) {
      let ultimoPunto = ruta[ruta.length - 1];
      let distanciaMinima = Infinity;
      let siguientePunto = null;
      let indiceSiguiente = -1;

      for (let i = 0; i < puntosRestantes.length; i++) {
        const distancia = calcularDistancia(ultimoPunto, puntosRestantes[i]);
        if (distancia < distanciaMinima) {
          distanciaMinima = distancia;
          siguientePunto = puntosRestantes[i];
          indiceSiguiente = i;
        }
      }

      if (siguientePunto) {
        ruta.push(siguientePunto);
        puntosRestantes.splice(indiceSiguiente, 1);
      }
    }

    return ruta;
  };

  const direccionesOrdenadas = useMemo(() => {
    if (!direcciones || !ubicacionInicio || direcciones.length === 0) return [];
    return calcularRutaOptima(direcciones, ubicacionInicio);
  }, [direcciones, ubicacionInicio]);

useEffect(() => {
  // VERIFICACIÓN MÁS ESTRICTA
  if (!window.google || !window.google.maps || !window.google.maps.DirectionsService) {
    console.log('Google Maps API not fully loaded, skipping directions calculation');
    return;
  }

  if (!ubicacionInicio || !direccionesOrdenadas || direccionesOrdenadas.length === 0) {
    return;
  }

  const service = new window.google.maps.DirectionsService();
  service.route(
    {
      origin: ubicacionInicio,
      destination: ubicacionInicio,
      waypoints: direccionesOrdenadas.map((d) => ({
        location: { lat: d.latitud, lng: d.longitud },
        stopover: true,
      })),
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    },
    (result, status) => {
      if (status === "OK") {
        setDirections(result);
      } else {
        console.error("Error en DirectionsService:", status);
      }
    }
  );
}, [googleLoaded, ubicacionInicio, direccionesOrdenadas]); // Añadir googleLoaded como dependencia

  // Handler para cuando el mapa se carga
  const handleMapLoad = (mapInstance) => {
    setMap(mapInstance);
  };

  // Calcular estadísticas
  const estadisticas = useMemo(() => {
    if (!direccionesOrdenadas.length || !ubicacionInicio) return null;
    
    let distanciaTotal = 0;
    if (direccionesOrdenadas.length > 0) {
      distanciaTotal += calcularDistancia(
        { latitud: ubicacionInicio.lat, longitud: ubicacionInicio.lng },
        direccionesOrdenadas[0]
      );
    }
    for (let i = 0; i < direccionesOrdenadas.length - 1; i++) {
      distanciaTotal += calcularDistancia(direccionesOrdenadas[i], direccionesOrdenadas[i + 1]);
    }
    
    return {
      totalParadas: direccionesOrdenadas.length,
      distanciaTotal: distanciaTotal.toFixed(1),
      tiempoEstimado: (distanciaTotal * 2.5).toFixed(0)
    };
  }, [direccionesOrdenadas, ubicacionInicio]);

  // ✅ 4. ESTADOS DE CARGA MEJORADOS - Basados en googleLoaded
  if (!googleLoaded) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Box textAlign="center">
              <CircularProgress size={60} thickness={4} sx={{ mb: 2, color: theme.palette.primary.main }} />
              <Typography variant="h6" color="text.secondary">
                Cargando Google Maps...
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10],
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
        }
      }}
    >
      <DialogTitle sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        padding: theme.spacing(2, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Navigation />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Ruta Optimizada
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Planificación de entregas inteligente
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: 'white',
            '&:hover': { background: alpha('#fff', 0.1) }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, background: theme.palette.background.default }}>
        {!direcciones || direcciones.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Card sx={{ textAlign: 'center', maxWidth: 400, mx: 'auto' }}>
              <CardContent>
                <Place sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No hay direcciones disponibles
                </Typography>
              </CardContent>
            </Card>
          </Box>
       ) : !googleLoaded ? ( // ✅ MOSTRAR LOADING SI NO ESTÁ CARGADO
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={60} thickness={4} sx={{ mb: 2, color: theme.palette.primary.main }} />
            <Typography variant="h6" color="text.secondary">
              Cargando mapa...
            </Typography>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={0} sx={{ height: '70vh' }}>
          {/* Mapa - SOLO SE RENDERIZA CUANDO googleLoaded ES true */}
          <Grid item xs={12} md={8} sx={{ height: '100%' }}>
            <GoogleMap
              mapContainerStyle={{ 
                height: '100%', 
                width: '100%',
                borderRadius: theme.spacing(1)
              }}
              center={ubicacionInicio || { lat: -32.95, lng: -60.66 }}
              zoom={12}
              options={{
                styles: [
                  {
                    featureType: "poi",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }]
                  }
                ]
              }}
              onLoad={handleMapLoad}
            >
                {directions && (
                  <DirectionsRenderer
                    directions={directions}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: theme.palette.primary.main,
                        strokeWeight: 5,
                        strokeOpacity: 0.7,
                      },
                    }}
                  />
                )}

                {ubicacionInicio && (
                  <Marker
                    position={ubicacionInicio}
                    icon={crearIcono("S", "#4caf50", true)}
                  />
                )}

                {direccionesOrdenadas.map((d, idx) => (
                  <Marker
                    key={d.id}
                    position={{ lat: d.latitud, lng: d.longitud }}
                    icon={crearIcono(String(idx + 1), theme.palette.primary.main, false)}
                  />
                ))}
              </GoogleMap>
            </Grid>

            {/* Panel de lista de paradas */}
            <Grid item xs={12} md={4} sx={{ height: '100%', overflow: 'hidden' }}>
              <Paper 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  background: alpha(theme.palette.background.paper, 0.95),
                  borderRadius: 0,
                  boxShadow: theme.shadows[4]
                }}
              >
                <Box sx={{ 
                  p: 2, 
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}08 100%)`,
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                  <Typography variant="h6" fontWeight="bold" color="primary" display="flex" alignItems="center" gap={1}>
                    <Place /> Itinerario de Entregas
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Orden optimizado de visitas
                  </Typography>
                </Box>

                <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                  {/* Punto de inicio */}
                  <ListItem 
                    sx={{ 
                      background: alpha('#4caf50', 0.1),
                      borderRadius: 2,
                      mb: 1,
                      border: `1px solid ${alpha('#4caf50', 0.2)}`
                    }}
                  >
                    <Box sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '50%', 
                      background: '#4caf50',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      mr: 2,
                      flexShrink: 0
                    }}>
                      S
                    </Box>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Punto de Inicio
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {ubicacionInicio ? 
                            `Lat: ${ubicacionInicio.lat.toFixed(4)}, Lng: ${ubicacionInicio.lng.toFixed(4)}` : 
                            'Ubicación no disponible'
                          }
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Lista de paradas */}
                  {direccionesOrdenadas.map((d, idx) => (
                    <ListItem 
                      key={d.id}
                      sx={{ 
                        background: theme.palette.background.paper,
                        borderRadius: 2,
                        mb: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.05),
                          borderColor: theme.palette.primary.main,
                          transform: 'translateX(4px)'
                        }
                      }}
                    >
                      <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        background: theme.palette.primary.main,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        mr: 2,
                        flexShrink: 0
                      }}>
                        {idx + 1}
                      </Box>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight="medium">
                            {d.nombre_cliente || d.direccion_entrega || `Parada ${idx + 1}`}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {d.direccion_entrega && d.nombre_cliente ? d.direccion_entrega : 
                             `Lat: ${parseFloat(d.latitud).toFixed(4)}, Lng: ${parseFloat(d.longitud).toFixed(4)}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MapaDireccionesModal;